import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Info } from '@digital-twins/design-system/icons'
import { SOCIAL_CREDITS_PER_150_POSTS } from '@digital-twins/shared-types'
import { CreditIcon } from '@/components/icons/CreditIcon'
import { Skeleton } from '@/components/ui/skeleton'
import { Text } from '@/components/ui/Text'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

import { SocialEstimateResponse, SocialTrackerCadence } from '@digital-twins/shared-types'
import { TFunction } from 'i18next'
import { formatSocialCredits, TopicCreditsEstimate } from './socialCredits'

export interface SocialUsageEstimateProps {
  /** The server estimate: per channel its next run's imports and each later update, from its run plan; null while unknown. */
  estimate: SocialEstimateResponse | null
  cadence: SocialTrackerCadence
  /** Relevant posts a week per channel the updates aim for. */
  runPostLimit: number
  /** Most posts per channel a first collection keeps. */
  firstCollectionPostLimit: number
  /** Credit ceilings (server estimate) and the observed month; null while unknown. */
  credits?: TopicCreditsEstimate | null
  /** Whole credits the first collections can hold (server estimate); null hides the row. */
  firstCollectionCredits?: number | null
  /** First estimate load: one skeleton line. */
  isLoading?: boolean
  /** A newer selection is loading: previous values stay on screen, dimmed. */
  isRefreshing?: boolean
  /** The estimate failed: rows hidden, nothing blocked. */
  hasError?: boolean
  /** Edit only, payer not the viewer: "Paid from Ana's credits.". */
  payerName?: string | null
  /** Review step only: the funds row (insufficient on create); null shows nothing. */
  funds?: ReactNode
  /** Create only: the first collection row shows a skeleton until the estimate loads. */
  expectsFirstCollection?: boolean
}

/**
 * What a first import reads, in whole days: the Topic's period (D23), the
 * same on every channel ("the last 30 days"); a source added to a saved
 * Topic reads the period so far, from its start.
 */
export function socialLookbackLabel(t: TFunction<'app'>, readDays: number): string {
  return t('socialListening.topicEstimate.lookbackDays', { count: Math.round(readDays) })
}

/**
 * The Topic dialog's cost card in two rows, one per Topic (no per-channel
 * lines), each a headline with its credit figures on the right. First
 * collection: the posts limit x channels and the period it reads; shown when
 * the next run imports something (a new channel, or a brand or search an edit
 * adds). Then: the cadence and each update's posts x channels (from the
 * estimate: a daily update keeps at least the run minimum), with "Each
 * update" and, on a schedule, "Each month" under their figures. The payer,
 * the charging note and, on review, the funds row close it. Prices nothing
 * itself.
 */
export function SocialUsageEstimate(props: SocialUsageEstimateProps) {
  const {
    estimate,
    cadence,
    runPostLimit,
    firstCollectionPostLimit,
    credits = null,
    firstCollectionCredits = null,
    isLoading = false,
    isRefreshing = false,
    hasError = false,
    payerName = null,
    funds = null,
    expectsFirstCollection = false,
  } = props

  const { t, i18n } = useTranslation('app')

  const number = (value: number) => value.toLocaleString(i18n.language)

  const channels = estimate?.channels ?? []
  const hasNewChannel = channels.some((channel) => channel.nextRun.firstImport !== null)
  const addedImports = channels.flatMap((channel) => channel.nextRun.addedImports)
  const importDays = channels.flatMap((channel) => [
    ...(channel.nextRun.firstImport ? [channel.nextRun.firstImport.readDays] : []),
    ...channel.nextRun.addedImports.map((added) => added.readDays),
  ])
  const hasImports = importDays.length > 0

  const addsBrands = addedImports.some((added) => added.brandName !== null)
  const addsSearches = addedImports.some((added) => added.brandName === null)
  const firstHeadline = hasNewChannel
    ? t('socialListening.form.firstCollection')
    : addsBrands && addsSearches
      ? t('socialListening.topicEstimate.firstAdded.both')
      : addsBrands
        ? t('socialListening.topicEstimate.firstAdded.brands')
        : t('socialListening.topicEstimate.firstAdded.searches')
  // "250 posts", or "250 posts × 2 channels": per channel said once.
  const postsAcross = (posts: number, channelCount: number) => {
    const postsLabel = t('socialListening.topicEstimate.posts', { count: posts, value: number(posts) })
    return channelCount > 1
      ? `${postsLabel} ${t('socialListening.topicEstimate.timesChannels', { channels: channelCount })}`
      : postsLabel
  }
  const importChannelCount = channels.filter(
    (channel) => channel.nextRun.firstImport !== null || channel.nextRun.addedImports.length > 0,
  ).length
  const firstDetail = hasImports
    ? t('socialListening.topicEstimate.firstDetail', {
        posts: postsAcross(firstCollectionPostLimit, importChannelCount),
        lookback: socialLookbackLabel(t, Math.max(...importDays)),
      })
    : null

  // Each update's posts per channel: the plan's (a daily update keeps at
  // least the run minimum); on demand it is the weekly rate, per week elapsed.
  const updatePosts =
    cadence === 'on_demand' || channels.length === 0
      ? runPostLimit
      : Math.max(...channels.map((channel) => channel.perUpdate.posts))
  const thenHeadline = t(`socialListening.topicEstimate.thenHeadline.${cadence}`)
  const thenDetail = t(`socialListening.topicEstimate.thenDetail.${cadence}`, {
    posts: postsAcross(updatePosts, channels.length),
  })

  const isObserved = credits !== null && credits.observedPerMonth !== null
  const upTo = (value: number) =>
    t('socialListening.credits.estimate.upTo', {
      count: value,
      value: formatSocialCredits(value, i18n.language, { ceiling: true }),
    })

  const monthFigure =
    credits === null || credits.maxPerMonth === null
      ? null
      : isObserved
        ? t('socialListening.credits.estimate.about', {
            count: credits.observedPerMonth as number,
            value: formatSocialCredits(credits.observedPerMonth, i18n.language),
          })
        : upTo(credits.maxPerMonth)

  const helpTrigger = (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="inline-flex rounded-sm text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label={t('socialListening.credits.estimate.helpAria')}
            data-testid="social-universe-estimate-help"
          >
            <Info className="h-3.5 w-3.5" aria-hidden />
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-[260px] text-xs" data-testid="social-universe-estimate-help-tip">
          {t('socialListening.credits.estimate.help', { rate: SOCIAL_CREDITS_PER_150_POSTS })}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )

  const isFirstLoading = isLoading && credits === null && !hasError
  const skeleton = <Skeleton className="h-5 w-28" data-testid="social-universe-estimate-loading" />
  const figureClass =
    'inline-flex items-center gap-1.5 whitespace-nowrap text-base font-semibold tabular-nums text-foreground'
  const creditIcon = <CreditIcon className="h-4 w-4 text-foreground" />

  const firstFigure =
    hasError || firstCollectionCredits === null ? (isFirstLoading ? skeleton : null) : (
      <span
        className={cn(figureClass, 'shrink-0', isRefreshing && 'opacity-60')}
        data-testid="social-universe-estimate-first-collection"
      >
        {creditIcon}
        {upTo(firstCollectionCredits)}
      </span>
    )

  const firstLoadingSection =
    !firstDetail && expectsFirstCollection && isFirstLoading ? (
      <section className="flex flex-col gap-1" data-testid="social-universe-estimate-first-pending">
        <div className="flex items-start justify-between gap-3">
          <Text as="p" size="sm" className="min-w-0 font-medium">
            {t('socialListening.form.firstCollection')}
          </Text>
          <Skeleton className="h-5 w-28" data-testid="social-universe-estimate-first-loading" />
        </div>
        <Skeleton className="h-4 w-48" />
      </section>
    ) : null

  const firstSection = firstDetail ? (
    <section className="flex flex-col gap-1" data-testid="social-universe-estimate-first">
      <div className="flex items-start justify-between gap-3">
        <Text
          as="p"
          size="sm"
          className="min-w-0 font-medium"
          data-testid="social-universe-estimate-first-headline"
        >
          {firstHeadline}
        </Text>
        {firstFigure}
      </div>
      <Text
        as="p"
        size="xs"
        color="muted"
        className="max-w-[46ch]"
        data-testid="social-universe-estimate-first-detail"
      >
        {firstDetail}
      </Text>
    </section>
  ) : null

  const thenFigure = hasError ? null : credits !== null ? (
    <div className="flex shrink-0 flex-col items-end gap-2">
      <div className={cn('flex flex-col items-end', isRefreshing && 'opacity-60')}>
        <span className={figureClass} data-testid="social-universe-estimate-per-update">
          {creditIcon}
          {upTo(credits.maxPerUpdate)}
        </span>
        <span className="flex items-center gap-1 text-xs text-muted-foreground">
          {t('socialListening.credits.estimate.perUpdateLabel')}
          {helpTrigger}
        </span>
      </div>
      {monthFigure !== null ? (
        <div
          className={cn('flex flex-col items-end', isRefreshing && 'opacity-60')}
          data-testid="social-universe-estimate-rows"
        >
          <span
            className="inline-flex items-center gap-1.5 whitespace-nowrap text-sm tabular-nums text-foreground"
            data-testid="social-universe-estimate-per-month"
          >
            {monthFigure}
          </span>
          <span className="text-xs text-muted-foreground">
            {t('socialListening.credits.estimate.perMonthLabel')}
          </span>
        </div>
      ) : null}
    </div>
  ) : isFirstLoading ? (
    skeleton
  ) : null

  const thenSection = estimate ? (
    <section
      className="flex flex-col gap-1"
      aria-busy={isRefreshing || undefined}
      data-testid="social-universe-estimate-then"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <Text as="p" size="sm" className="font-medium" data-testid="social-universe-estimate-updates">
            {thenHeadline}
          </Text>
          <Text as="p" size="xs" color="muted" data-testid="social-universe-estimate-then-detail">
            {thenDetail}
          </Text>
        </div>
        {thenFigure}
      </div>
    </section>
  ) : null

  const sentence =
    hasError || credits === null
      ? null
      : isObserved
        ? t('socialListening.credits.estimate.observedBasis')
        : null

  // The only live region (a11y): one short line with the figures, so a change
  // is announced without re-reading the whole panel. Empty until they load.
  const liveFirst =
    hasError || firstCollectionCredits === null
      ? ''
      : `${t('socialListening.credits.estimate.liveFirst', { value: upTo(firstCollectionCredits) })} `
  const liveRest =
    hasError || credits === null
      ? ''
      : monthFigure === null
        ? t('socialListening.credits.estimate.liveOnDemand', {
            perUpdate: upTo(credits.maxPerUpdate),
          })
        : t('socialListening.credits.estimate.live', {
            perUpdate: upTo(credits.maxPerUpdate),
            perMonth: monthFigure,
          })
  const liveSummary = `${liveFirst}${liveRest}`.trim()

  const firstRow = firstSection ?? firstLoadingSection
  const thenRow = thenSection ?? (firstLoadingSection ? skeleton : null)

  if (!firstRow && !thenSection && funds === null) {
    return isFirstLoading ? skeleton : null
  }

  return (
    <div
      className="flex flex-col gap-3"
      data-testid="social-universe-estimate"
    >
      <p
        className="sr-only"
        aria-live="polite"
        aria-atomic="true"
        data-testid="social-universe-estimate-live"
      >
        {liveSummary}
      </p>
      {firstRow}
      {firstRow && thenRow ? <div className="h-px bg-border" aria-hidden="true" /> : null}
      {thenRow}
      {sentence || payerName ? (
        <div className="flex flex-col gap-0.5 border-t border-border pt-3">
          {sentence ? (
            <Text size="xs" color="muted" data-testid="social-universe-estimate-basis">
              {sentence}
            </Text>
          ) : null}
          {payerName ? (
            <Text size="xs" color="muted" data-testid="social-universe-estimate-payer">
              {t('socialListening.credits.payer', { name: payerName })}
            </Text>
          ) : null}
        </div>
      ) : null}
      {funds ? (
        <div className="border-t border-border pt-3" data-testid="social-universe-estimate-funds">
          {funds}
        </div>
      ) : null}
    </div>
  )
}
